import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Accordion, Nav, NavItem } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { LinkContainer } from "react-router-bootstrap";
import { Link, useLocation } from "react-router-dom";

import { usePrefetchCalendar } from "utils/hooks/usePrefetchCalendar";
import { useSubjects } from "utils/hooks/useSubjects";

import IconBook from "components/Icons/IconBook/IconBook";
import IconPencil from "components/Icons/IconPencil/IconPencil";
import { IconStrawberry } from "components/Icons/Strawberry";
import IconHelp from "../Icons/IconHelp/IconHelp";
import IconSettings from "../Icons/IconSettings/IconSettings";
import IconTrophy from "../Icons/IconTrophy/IconTrophy";

import { SUBJECT_LINKS } from "const";
import { useAppSelector } from "store/hooks";
import { getFrom } from "store/slices/envelopeNavigation";
import { useContentAccess } from "utils/hooks/useContentAccess";

export const Menu = () => {
  const { t } = useTranslation(["common"]);

  const navMenuCategories = [
    {
      id: "training",
      translationKey: "menu.training",
      icon: <IconStrawberry />,
      links: [
        { path: "/", label: t("pageNameHome") },
        { path: "/kalendar", label: t("pageNameCalendar") },
        { path: "/historie", label: t("pageNameHistory") },
        { path: "/ulohy", label: t("pageNameEnvelopesAndTasks") },
        { path: "/ulozene", label: t("pageNameBookmarks") },
        { path: "/vyrazene", label: t("pageNameBlacklist") },
        { path: "/reklamovane-odpovedi", label: t("pageNameUnrecognizedAnswers") },
      ] as const,
    },
    {
      id: "tutoring",
      translationKey: "menu.tutoring",
      icon: <IconBook width={18} height={18} />,
      links: [{ path: "/videotipy", label: t("pageNameVideo") }] as const,
    },
  ];

  // Samostatná sekce pod Odznaky a statistiky (dle návrhu desktop-with-left-menu).
  const graduationReadingCategory = {
    id: "graduationReading",
    translationKey: "menu.graduationReading",
    icon: <IconBook width={18} height={18} />,
    links: [
      // Pozor: cesta nesmí být přesně "/knihy" — shodovala by se s activeKey (první slug URL)
      // a odkaz by pak byl aktivní na všech podstránkách knih
      { path: "/knihy/muj-vyber", label: t("pageNameBooksMySelection") },
      { path: "/knihy/vsechny", label: t("pageNameBooksAll") },
      // AIDEV-NOTE: Odemčeno na pokyn zadavatele 28. 8. 2026. Obsah ve stage je zatím
      // vzorový a odborně neschválený — zamčení bylo pojistka právě proti tomu, takže
      // než sekci uvidí prod, musí do CMS přijít schválené kartičky a ukázky. [TD-346]
      { path: "/knihy/neumelecke-texty", label: t("pageNameBooksNonArtistic") },
    ] as const,
  };

  const { pathname } = useLocation();
  const envelopeOrigin = useAppSelector(getFrom);

  const [activeKey, setActiveKey] = useState("/");
  const envelopeFrom = useRef<string | null>(null);
  const { rawData: subjects } = useSubjects();
  const { graduationAccess, tutoringVisible } = useContentAccess();
  const { preloadChunk, prefetchCalendar } = usePrefetchCalendar();

  // Chunk se stahuje při najetí, data až při dotyku nebo zaostření, tedy dřív než klik. [TD-476]
  const calendarPrefetchHandlers = {
    onPointerDown: prefetchCalendar,
    onMouseEnter: preloadChunk,
    onFocus: prefetchCalendar,
  };

  const graduationMasterclassCategory = {
    id: "graduationMasterclass",
    translationKey: "pageNameMasterclass",
    icon: <IconPencil width={18} height={18} />,
    links: [
      { path: "/videotipy", label: t("pageNameVideoTips") },
      // AIDEV-NOTE: `path` stays the real target even when locked — it drives the section
      // highlight and `defaultActiveKey`; `/kompletni-kurz` must never light this section up.
      // The href is diverted in renderMenuCategory instead. [TD-376]
      { path: "/masterclass", label: t("menu.masterclassVideos"), isLocked: graduationAccess === "upgrade" },
    ] as const,
  };

  const locationFirstSlug = pathname.split("/")[1];

  useEffect(() => {
    if (envelopeOrigin !== envelopeFrom.current) {
      envelopeFrom.current = envelopeOrigin;
    }

    if (pathname.includes("/ulohy/obalka")) {
      setActiveKey(envelopeFrom.current ?? "/ulohy");
      return;
    }

    setActiveKey(`/${pathname.split("/")[1]}`);
  }, [pathname, envelopeOrigin]);

  const enrichedNavMenuCategories = useMemo(() => {
    // AIDEV-NOTE: Both flags derive from `grade` in utils/contentAccess, so "tutoring hidden"
    // and "masterclass section hidden" can never coincide and Videotipy never vanish
    // from the menu. [TD-376]
    if (!tutoringVisible) return navMenuCategories.filter(category => category.id !== "tutoring");
    if (!subjects) return navMenuCategories;

    const tutoringLinks: Array<{ path: string; label: string }> = [];
    for (const subject of subjects) {
      const abb = subject.abbreviation;
      if (!abb) continue;

      const link = SUBJECT_LINKS[abb];
      if (!link) continue;

      tutoringLinks.push(link);
    }

    tutoringLinks.sort((a, b) => a.label.localeCompare(b.label));

    return navMenuCategories.map(category =>
      category.id === "tutoring" ? { ...category, links: [...tutoringLinks, ...category.links] } : category,
    );
  }, [subjects, tutoringVisible]);

  // AIDEV-NOTE: Computed from the filtered list: tutoring and masterclass both own `/videotipy`, so a
  // `find` over the raw categories would return "tutoring" and the graduate's section
  // would never open on that page. [TD-376]
  const initialMenuCategoryId = [
    ...enrichedNavMenuCategories,
    graduationReadingCategory,
    graduationMasterclassCategory,
  ].find(category => category.links.some(link => locationFirstSlug === link.path.split("/")[1]))?.id;

  const renderMenuCategory = (category: {
    id: string;
    translationKey: string;
    icon: ReactNode;
    /**
     * `isDisabled` nese jen položka, která cíl zatím nemá — viz Neumělecké texty.
     * `isLocked` is the light graduate's case: the target exists, but the item leads to the course offer.
     */
    links: ReadonlyArray<{ path: string; label: string; isDisabled?: boolean; isLocked?: boolean }>;
  }) => (
    <Accordion.Item
      eventKey={category.id}
      key={category.id}
      className={
        category.links.some(link => link.path === activeKey || `/${link.path.split("/")[1]}` === activeKey)
          ? "is-highlighted"
          : undefined
      }
    >
      <Accordion.Header>
        {category.icon}
        {t(category.translationKey)}
      </Accordion.Header>
      <Accordion.Body>
        {category.links.map(link =>
          link.isLocked ? (
            <NavItem key={link.path}>
              <Nav.Link as={Link} to="/kompletni-kurz" className="nav-link--locked" title={t("menu.lockedHint")}>
                {link.label}
              </Nav.Link>
            </NavItem>
          ) : link.isDisabled ? (
            <NavItem key={link.path}>
              <Nav.Link disabled>{link.label}</Nav.Link>
            </NavItem>
          ) : (
            <NavItem key={link.path}>
              <LinkContainer to={link.path}>
                <Nav.Link {...(link.path === "/kalendar" ? calendarPrefetchHandlers : undefined)}>
                  {link.label}
                </Nav.Link>
              </LinkContainer>
            </NavItem>
          ),
        )}
      </Accordion.Body>
    </Accordion.Item>
  );

  return (
    <Nav className={"flex-column w-100 nav--wrapper"} activeKey={activeKey}>
      <Accordion defaultActiveKey={initialMenuCategoryId}>
        {enrichedNavMenuCategories.map(category => renderMenuCategory(category))}
      </Accordion>
      {/* Odznaky a statistiky */}
      <NavItem className="accordion-item">
        <div className="accordion-header">
          <LinkContainer to="/statistiky" className="accordion-button collapsed">
            <Nav.Link>
              <IconTrophy width={18} height={18} />
              {t("pageNameMyAchievements")}
            </Nav.Link>
          </LinkContainer>
        </div>
      </NavItem>
      {/* AIDEV-NOTE: Tenhle oddělovač patří všem ročníkům, proto stojí mimo podmínku —
          bez maturitní sekce by jinak „Odznaky a statistiky" navazovaly rovnou na
          „Nastavení" bez předělu, a to se týká většiny uživatelů. Jediná čára uvnitř
          podmínky teď stojí až za masterclassem, před Nastavením — mezi Maturitní
          četbou a masterclassem podle Figmy (13207:42329) žádná není. [TD-351, TD-327 S-04] */}
      <hr />
      {graduationAccess !== "hidden" && (
        <>
          {graduationAccess === "granted" ? (
            <Accordion defaultActiveKey={initialMenuCategoryId}>
              {renderMenuCategory(graduationReadingCategory)}
            </Accordion>
          ) : (
            // Light maturant nemá u maturitní četby co rozbalovat — všechny podstránky by
            // vedly na tutéž nabídku upgradu, proto jedna položka místo accordionu.
            //
            // AIDEV-NOTE: Obě zamčené položky míří na tutéž `/kompletni-kurz`, proto tu není
            // `LinkContainer`: ten porovnává cestu s URL a se společným cílem by označil za
            // aktivní OBĚ naráz — kliknutím na četbu by se rozsvítila i masterclass. Prostý
            // `Link` aktivní stav neřeší a zamčená položka ho ani nemá mít. [TD-351, TD-374]
            // The same applies to the locked `isLocked` sub-item inside Accordion.Body. [TD-376]
            <NavItem className="accordion-item">
              <div className="accordion-header">
                <Nav.Link
                  as={Link}
                  to="/kompletni-kurz"
                  className="accordion-button collapsed nav-link--locked"
                  title={t("menu.lockedHint")}
                >
                  <IconBook width={18} height={18} />
                  {t("menu.graduationReading")}
                </Nav.Link>
              </div>
            </NavItem>
          )}
          <Accordion defaultActiveKey={initialMenuCategoryId}>
            {renderMenuCategory(graduationMasterclassCategory)}
          </Accordion>
          <hr />
        </>
      )}
      <NavItem className="accordion-item">
        <div className="accordion-header">
          <LinkContainer to="/nastaveni" className="accordion-button collapsed">
            <Nav.Link>
              <IconSettings width={18} height={18} />
              {t("pageNameSettings")}
            </Nav.Link>
          </LinkContainer>
        </div>
      </NavItem>

      <NavItem className="accordion-item">
        <div className="accordion-header">
          <LinkContainer to="/dotazy" className="accordion-button collapsed">
            <Nav.Link>
              <IconHelp width={18} height={18} />
              {t("pageNameHelp")}
            </Nav.Link>
          </LinkContainer>
        </div>
      </NavItem>

      {import.meta.env.VITE_ENV !== "production" ? (
        <NavItem className="accordion-item">
          <div className="accordion-header">
            <LinkContainer to="/dev" className="accordion-button collapsed">
              <Nav.Link>
                <IconSettings width={18} height={18} />
                Dev
              </Nav.Link>
            </LinkContainer>
          </div>
        </NavItem>
      ) : null}
    </Nav>
  );
};

export default Menu;
